import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, InputNumber, message, Row, Col, Space, Table } from "antd";
import ModalCommon from "../../components/Modal";
import { TEXT_DEF } from "../../constant/text";
import API, { CONFIG, UPDATE_CONFIG } from "../../network/api";

/** Key API: tỷ lệ quy đổi điểm thưởng (thập phân 0–1, ví dụ 0.53 = 53%) */
const COIN_RATE = "COIN_RATE";

// VIP Keys theo API format
const VIP_KEYS = {
  VIP_0_5: "VIP 0 - 5",
  VIP_6_11: "VIP 6 - 11",
  VIP_12_15: "VIP 12 - 15",
  VIP_16_20: "VIP 16 - 20",
};

// Map key API sang Label hiển thị
const VIP_LABELS = {
  VIP_0_5: "VIP 0 - 5",
  VIP_6_11: "VIP 6 - 11",
  VIP_12_15: "VIP 12 - 15",
  VIP_16_20: "VIP 16 - 20",
};

const DEFAULT_GAMEPLAY = {
  turnDuration: 60,
  speeds: [1, 2, 3, 4, 5],
  phasesDuration: [20, 10, 10, 10, 5],
  totalSymbols: 25,
};

const GP_TEXT = {
  secondsPerTurn: "Số giây mỗi lượt",
  fallStagesTitle: "Cấu hình tốc độ rơi theo giai đoạn",
  timeRangeCol: "Thời gian theo giai đoạn",
  fallSpeedCol: "Tốc độ rơi",
  totalSymbolsLabel: "Số lượng vật phẩm",
};

const buildRangesFromDurations = (turn, durations) => {
  let remaining = Number(turn) || 0;
  return durations.map((duration) => {
    const from = remaining;
    const to = Math.max(from - (Number(duration) || 0), 0);
    remaining = to;
    return { timeFrom: from, timeTo: to };
  });
};

const SetupScore = () => {
  const [isShowModalConfirm, setIsShowModalConfirm] = useState(false);
  const [isShowModalSuccess, setIsShowModalSuccess] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // State lưu dữ liệu hiển thị theo wireframe
  // Format: { [VIP_KEY]: { maxScore: number, percent: number } }
  const [vipConfigData, setVipConfigData] = useState({});

  // Cấu hình chung (Vòng cược rút tiền)
  const [preWithdrawRounds, setPreWithdrawRounds] = useState(0);

  // Cơ chế lượt chơi (các field nằm thẳng trên config: turnDuration, speeds, phasesDuration, …)
  const [turnDuration, setTurnDuration] = useState(DEFAULT_GAMEPLAY.turnDuration);
  const [speeds, setSpeeds] = useState(DEFAULT_GAMEPLAY.speeds);
  const [stageRanges, setStageRanges] = useState(
    buildRangesFromDurations(DEFAULT_GAMEPLAY.turnDuration, DEFAULT_GAMEPLAY.phasesDuration)
  );
  const [totalSymbols, setTotalSymbols] = useState(DEFAULT_GAMEPLAY.totalSymbols);

  // Giữ lại config gốc
  const [originalConfig, setOriginalConfig] = useState({});

  const getDataConfig = useCallback(async () => {
    try {
      const res = await API.get(`${CONFIG}`);
      const data = res.data?.data || res.data;
      setOriginalConfig(data);

      // 1. Load Pre-withdraw rounds
      if (data.preWithdrawRounds !== undefined) {
        setPreWithdrawRounds(data.preWithdrawRounds);
      }

      // 2. Load VIP Data từ vipLevels
      const vipLevels = data.vipLevels || {};
      
      const newVipConfig = {};
      Object.keys(VIP_KEYS).forEach(key => {
        const vipData = vipLevels[key];
        
        // Lấy MAX_SCORE từ vipLevels
        const maxScore = vipData?.MAX_SCORE || 0;
        
        // Lấy COIN_RATE và convert sang phần trăm (COIN_RATE là số thập phân, ví dụ 0.53 = 53%)
        const rateDecimal = vipData?.[COIN_RATE] || 0;
        const percent = Math.round(rateDecimal * 100);

        newVipConfig[key] = {
            maxScore: maxScore,
            percent: percent
        };
      });
      setVipConfigData(newVipConfig);

      const legacyGp = data.gameplay || {};
      const loadedTurn =
        data.turnDuration ??
        data.secondsPerTurn ??
        legacyGp.turnDuration ??
        legacyGp.secondsPerTurn ??
        DEFAULT_GAMEPLAY.turnDuration;
      setTurnDuration(
        loadedTurn !== undefined && loadedTurn !== null
          ? Number(loadedTurn)
          : DEFAULT_GAMEPLAY.turnDuration
      );
      const fallStages =
        Array.isArray(data.fallSpeedStages) && data.fallSpeedStages.length > 0
          ? data.fallSpeedStages
          : Array.isArray(legacyGp.fallSpeedStages) && legacyGp.fallSpeedStages.length > 0
            ? legacyGp.fallSpeedStages
            : null;
      const loadedSpeeds = Array.isArray(data.speeds) && data.speeds.length > 0
        ? data.speeds.map((value) => Number(value) || 0)
        : Array.isArray(legacyGp.speeds) && legacyGp.speeds.length > 0
          ? legacyGp.speeds.map((value) => Number(value) || 0)
          : Array.isArray(fallStages) && fallStages.length > 0
            ? fallStages.map((row, i) => Number(row.fallSpeed ?? row.speed ?? DEFAULT_GAMEPLAY.speeds[i] ?? 0))
            : DEFAULT_GAMEPLAY.speeds;
      const loadedPhasesDuration = Array.isArray(data.phasesDuration) && data.phasesDuration.length > 0
        ? data.phasesDuration.map((value) => Number(value) || 0)
        : Array.isArray(legacyGp.phasesDuration) && legacyGp.phasesDuration.length > 0
          ? legacyGp.phasesDuration.map((value) => Number(value) || 0)
          : Array.isArray(fallStages) && fallStages.length > 0
            ? fallStages.map((row, i) => {
                const fallback = DEFAULT_GAMEPLAY.phasesDuration[i] ?? 0;
                if (row.timeFrom === undefined || row.timeTo === undefined) return fallback;
                return Math.abs((Number(row.timeFrom) || 0) - (Number(row.timeTo) || 0));
              })
            : DEFAULT_GAMEPLAY.phasesDuration;

      const stagesCount = Math.max(loadedSpeeds.length, loadedPhasesDuration.length, DEFAULT_GAMEPLAY.speeds.length);
      setSpeeds(
        Array.from({ length: stagesCount }, (_, i) => Number(loadedSpeeds[i] ?? DEFAULT_GAMEPLAY.speeds[i] ?? 0))
      );
      if (Array.isArray(fallStages) && fallStages.length > 0) {
        setStageRanges(
          fallStages.map((row, i) => ({
            timeFrom: Number(row.timeFrom ?? loadedTurn ?? DEFAULT_GAMEPLAY.turnDuration) || 0,
            timeTo: Number(row.timeTo ?? ((Number(row.timeFrom) || 0) - (loadedPhasesDuration[i] || 0))) || 0,
          }))
        );
      } else {
        const durations = Array.from(
          { length: stagesCount },
          (_, i) => Number(loadedPhasesDuration[i] ?? DEFAULT_GAMEPLAY.phasesDuration[i] ?? 0)
        );
        setStageRanges(buildRangesFromDurations(loadedTurn, durations));
      }
      
      const loadedSymbols =
        data.totalSymbols ??
        data.itemCount ??
        legacyGp.totalSymbols ??
        legacyGp.itemCount ??
        DEFAULT_GAMEPLAY.totalSymbols;
      setTotalSymbols(
        loadedSymbols !== undefined && loadedSymbols !== null
          ? Number(loadedSymbols)
          : DEFAULT_GAMEPLAY.totalSymbols
      );

    } catch (err) {
      console.log(err);
      messageApi.error("Không thể tải dữ liệu cấu hình");
    }
  }, [messageApi]);

  useEffect(() => {
    getDataConfig();
  }, [getDataConfig]);

  const handleUpdateConfig = async () => {
    try {
      setIsShowModalConfirm(false);

      // Rebuild VIP Levels Data theo format API
      const newVipLevels = {};

      Object.keys(vipConfigData).forEach(key => {
          const item = vipConfigData[key];
          const existingVipData = originalConfig.vipLevels?.[key] || {};
          
          // Giữ nguyên MIN_SCORE nếu có, nếu không thì set mặc định
          const minScore = existingVipData.MIN_SCORE || 0.1;
          
          // Convert phần trăm sang COIN_RATE (ví dụ: 53% = 0.53)
          const rateDecimal = item.percent > 0 ? Number((item.percent / 100).toFixed(2)) : 0;

          newVipLevels[key] = {
              MAX_SCORE: item.maxScore || 0,
              MIN_SCORE: minScore,
              [COIN_RATE]: rateDecimal
          };
      });
      const computedPhasesDuration = stageRanges.map((row) => {
        const from = Number(row.timeFrom) || 0;
        const to = Number(row.timeTo) || 0;
        return from >= to ? from - to : 0;
      });
      const hasInvalidRange = stageRanges.some((row) => (Number(row.timeFrom) || 0) <= (Number(row.timeTo) || 0));
      if (hasInvalidRange) {
        messageApi.error("Mốc thời gian không hợp lệ: mốc trái phải lớn hơn mốc phải");
        return;
      }
      const T0 = Number(turnDuration) || 0;
      let chainOk = true;
      for (let i = 0; i < stageRanges.length; i++) {
        const from = Number(stageRanges[i].timeFrom) || 0;
        if (i === 0 && from !== T0) chainOk = false;
        if (i > 0 && from !== (Number(stageRanges[i - 1].timeTo) || 0)) chainOk = false;
      }
      const lastTo = Number(stageRanges[stageRanges.length - 1]?.timeTo) || 0;
      if (lastTo !== 0) chainOk = false;
      if (!chainOk) {
        messageApi.error(
          "Các giai đoạn phải nối tiếp nhau (không chồng lấn, không hở): mỗi mốc phải khớp với giai đoạn kế, giai đoạn cuối kết thúc tại 0 giây."
        );
        return;
      }
      const totalPhaseSeconds = computedPhasesDuration.reduce((sum, value) => sum + value, 0);
      if (totalPhaseSeconds > (Number(turnDuration) || 0)) {
        messageApi.error("Tổng thời gian theo giai đoạn không được vượt quá Số giây mỗi lượt");
        return;
      }

      const { gameplay: _omitGameplay, ...configBase } = originalConfig;
      const newConfig = {
        ...configBase,
        preWithdrawRounds: preWithdrawRounds,
        vipLevels: newVipLevels,
        turnDuration: Number(turnDuration) || 0,
        speeds: speeds.map((value) => Number(value) || 0),
        phasesDuration: computedPhasesDuration,
        totalSymbols: Number(totalSymbols) || 0,
      };

      const body = {
        config: newConfig,
      };

      await API.patch(`${UPDATE_CONFIG}`, body);
      setIsShowModalSuccess(true);

      setTimeout(() => {
        getDataConfig();
      }, 1000);
    } catch (err) {
      console.log(err);
      messageApi.error(err?.response?.data?.message || "Cập nhật thất bại");
      setIsShowModalSuccess(false);
    }
  };

  const handleShowModal = () => setIsShowModalConfirm(true);

  const handleVipChange = (key, field, value) => {
      setVipConfigData(prev => ({
          ...prev,
          [key]: {
              ...prev[key],
              [field]: Number(value) || 0
          }
      }));
  };

  const handleSpeedChange = (index, value) => {
    setSpeeds((prev) => {
      const next = [...prev];
      next[index] = Number(value) || 0;
      return next;
    });
  };

  const handleStageRangeChange = (index, field, value) => {
    setStageRanges((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: Number(value) || 0,
      };
      return next;
    });
  };

  const phaseDurations = useMemo(
    () =>
      stageRanges.map((row) => {
        const from = Number(row.timeFrom) || 0;
        const to = Number(row.timeTo) || 0;
        return from >= to ? from - to : 0;
      }),
    [stageRanges]
  );
  const totalPhaseSeconds = useMemo(
    () => phaseDurations.reduce((sum, value) => sum + value, 0),
    [phaseDurations]
  );
  const stageValidationError = useMemo(() => {
    const hasInvalidRange = stageRanges.some(
      (row) => (Number(row.timeFrom) || 0) <= (Number(row.timeTo) || 0)
    );
    if (hasInvalidRange) {
      return "Mốc thời gian không hợp lệ: mốc trái phải lớn hơn mốc phải";
    }

    const T0 = Number(turnDuration) || 0;
    for (let i = 0; i < stageRanges.length; i++) {
      const from = Number(stageRanges[i].timeFrom) || 0;
      if (i === 0 && from !== T0) {
        return "Mốc đầu tiên phải bằng Số giây mỗi lượt";
      }
      if (i > 0 && from !== (Number(stageRanges[i - 1].timeTo) || 0)) {
        return "Các giai đoạn phải nối tiếp nhau, không chồng lấn hoặc bị hở";
      }
    }
    const lastTo = Number(stageRanges[stageRanges.length - 1]?.timeTo) || 0;
    if (lastTo !== 0) {
      return "Giai đoạn cuối phải kết thúc tại 0 giây";
    }
    if (totalPhaseSeconds > (Number(turnDuration) || 0)) {
      return "Tổng thời gian theo giai đoạn không được vượt quá Số giây mỗi lượt";
    }
    return "";
  }, [stageRanges, totalPhaseSeconds, turnDuration]);
  const handleStageBlur = () => {
    if (stageValidationError) {
      messageApi.error(stageValidationError);
    }
  };

  const fallSpeedDataSource = speeds.map((speedValue, rowIndex) => ({
    speedValue,
    range: stageRanges[rowIndex] || { timeFrom: 0, timeTo: 0 },
    phaseDuration: Number(phaseDurations[rowIndex]) || 0,
    rowIndex,
    key: rowIndex,
  }));

  const fallSpeedColumns = [
    {
      title: GP_TEXT.timeRangeCol,
      key: "range",
      width: 320,
      render: (_, record) => (
        <Space size={4} align="center" wrap={false}>
          <InputNumber
            min={0}
            style={{ width: 80 }}
            value={record.range.timeFrom}
            onChange={(val) => handleStageRangeChange(record.rowIndex, "timeFrom", val)}
            onBlur={handleStageBlur}
          />
          <span>-</span>
          <InputNumber
            min={0}
            style={{ width: 80 }}
            value={record.range.timeTo}
            onChange={(val) => handleStageRangeChange(record.rowIndex, "timeTo", val)}
            onBlur={handleStageBlur}
          />
          <span>giây</span>
          <span style={{ color: "#6b7280", fontSize: 12 }}>(= {record.phaseDuration}s)</span>
        </Space>
      ),
    },
    {
      title: GP_TEXT.fallSpeedCol,
      key: "fallSpeed",
      width: 96,
      render: (_, record) => (
        <InputNumber
          min={0}
          style={{ width: 80 }}
          value={record.speedValue}
          onChange={(val) => handleSpeedChange(record.rowIndex, val)}
        />
      ),
    },
  ];

  return (
    <div className="col-12" style={{ padding: "20px" }}>
      {contextHolder}

      {/* SECTION MAIN: CẤU HÌNH NHƯ WIREFRAME */}
      <div style={{ marginBottom: "40px", border: "1px solid #d9d9d9", padding: "20px", borderRadius: "8px" }}>
        
        {/* Header Row */}
        <Row gutter={24} style={{ marginBottom: "15px", fontWeight: "bold", textTransform: "uppercase" }}>
            <Col span={6}></Col> {/* Cột VIP Label */}
            <Col span={9}>ĐIỂM THƯỞNG MAX</Col>
            <Col span={9}>% ĐIỂM THƯỞNG</Col>
        </Row>

        {/* Rows for each VIP */}
        {Object.keys(VIP_KEYS).map((key) => (
            <Row key={key} gutter={24} style={{ marginBottom: "15px", alignItems: "center" }}>
                <Col span={6} style={{ fontWeight: 500 }}>
                    {VIP_LABELS[key] || key}:
                </Col>
                <Col span={9}>
                    <InputNumber
                        style={{ width: "100%" }}
                        value={vipConfigData[key]?.maxScore}
                        onChange={(val) => handleVipChange(key, 'maxScore', val)}
                        placeholder="Nhập điểm max"
                    />
                </Col>
                <Col span={9}>
                    <InputNumber
                        style={{ width: "100%" }}
                        value={vipConfigData[key]?.percent}
                        onChange={(val) => handleVipChange(key, 'percent', val)}
                        formatter={(value) => `${value}%`}
                        parser={(value) => value.replace('%', '')}
                        min={0}
                        max={100}
                        placeholder="Nhập %"
                    />
                </Col>
            </Row>
        ))}

        {/* Footer Row: Pre-withdraw */}
        <Row gutter={24} style={{ marginTop: "30px", alignItems: "center" }}>
             <Col span={6} style={{ fontWeight: 500 }}>
                Vòng cược trước khi rút:
             </Col>
             <Col span={9}>
                <InputNumber
                    style={{ width: "100px" }} // Wireframe vẽ ô này nhỏ
                    value={preWithdrawRounds}
                    onChange={(val) => setPreWithdrawRounds(Number(val) || 0)}
                />
             </Col>
        </Row>

      </div>

      {/* Cơ chế lượt chơi: giây/lượt, tốc độ rơi theo giai đoạn, giỏ hứng, số vật phẩm */}
      <div style={{ marginBottom: "40px", border: "1px solid #d9d9d9", padding: "20px", borderRadius: "8px" }}>
        <div style={{ marginBottom: 20, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontWeight: 600 }}>{GP_TEXT.secondsPerTurn}</span>
          <InputNumber
            min={1}
            max={600}
            style={{ width: 88 }}
            value={turnDuration}
            onChange={(val) => setTurnDuration(Number(val) || 0)}
            onBlur={handleStageBlur}
          />
          <span>giây</span>
        </div>

        <div style={{ fontWeight: "bold", marginBottom: "12px", textTransform: "uppercase" }}>
          {GP_TEXT.fallStagesTitle}
        </div>
        <div style={{ width: "max-content", maxWidth: "100%" }}>
          <Table
            size="small"
            bordered
            pagination={false}
            dataSource={fallSpeedDataSource}
            columns={fallSpeedColumns}
            tableLayout="fixed"
            style={{ width: "max-content" }}
          />
        </div>
        <div style={{ marginTop: 8, color: totalPhaseSeconds > (Number(turnDuration) || 0) ? "#dc2626" : "#6b7280" }}>
          Tổng thời gian giai đoạn: {totalPhaseSeconds}s / {Number(turnDuration) || 0}s
        </div>
        {!!stageValidationError && (
          <div style={{ marginTop: 6, color: "#dc2626" }}>
            {stageValidationError}
          </div>
        )}

        
        <Row gutter={24} style={{ alignItems: "center" }}>
          <Col span={8} style={{ fontWeight: 600 }}>
            {GP_TEXT.totalSymbolsLabel}
          </Col>
          <Col span={16}>
            <InputNumber
              style={{ width: "100%", maxWidth: 200 }}
              min={0}
              value={totalSymbols}
              onChange={(val) => setTotalSymbols(Number(val) || 0)}
            />
          </Col>
        </Row>
      </div>

      <Button
        type="primary"
        style={{ marginTop: 20 }}
        disabled={!!stageValidationError}
        onClick={handleShowModal}
      >
        Cập nhật
      </Button>

      {isShowModalConfirm && (
        <ModalCommon
          isShow={isShowModalConfirm}
          message={TEXT_DEF.text0062}
          txtBtnAcc={TEXT_DEF.text0008}
          textBtnClose={TEXT_DEF.text0023}
          actionNext={handleUpdateConfig}
          close={() => setIsShowModalConfirm(false)}
        />
      )}
      {isShowModalSuccess && (
        <ModalCommon
          isShow={isShowModalSuccess}
          message={TEXT_DEF.text0063}
          txtBtnAcc={TEXT_DEF.text0007}
          actionNext={() => {
            setIsShowModalSuccess(false);
            getDataConfig();
          }}
          close={() => {
            setIsShowModalSuccess(false);
            getDataConfig();
          }}
        />
      )}
    </div>
  );
};

export default SetupScore;